// Copyright (c) 2026 Goffy. All Rights Reserved.

/**
 * Cross-device "current hole" following. A user's devices share one position per round: the
 * hole they last viewed or scored on any of them. These helpers decide when a device should
 * follow a position that arrived from the server.
 */

/** Ignore remote positions this long after the user navigated on this device, so two devices
 *  touched at the same moment do not ping-pong. */
export const LOCAL_NAVIGATION_GRACE_MS = 3000;

export type RemotePosition = {
  deviceId?: string | null;
  hole?: number | null;
};

export type FollowContext = {
  /** This device's id. */
  deviceId: string;
  /** The hole currently shown on this device. */
  currentHole: number;
  /** When the user last navigated on this device (ms epoch), or 0 if never. */
  lastLocalNavigationAt: number;
  now: number;
  holesCount: number;
};

/**
 * The hole to navigate to for a `round.position.updated` frame, or null to stay put:
 * the echo of this device's own navigation, an unchanged hole, an out-of-range hole, and any
 * position arriving inside the local-navigation grace window are all ignored.
 */
export const resolveRemotePositionTarget = (
  position: RemotePosition,
  context: FollowContext,
): number | null => {
  const hole = position.hole;
  if (typeof hole !== 'number' || !Number.isInteger(hole) || hole < 1 || hole > context.holesCount) {
    return null;
  }
  if (position.deviceId && position.deviceId === context.deviceId) return null;
  if (hole === context.currentHole) return null;
  if (context.lastLocalNavigationAt > 0 && context.now - context.lastLocalNavigationAt < LOCAL_NAVIGATION_GRACE_MS) {
    return null;
  }
  return hole;
};

/**
 * The hole to open on when the round's persisted position (`round.state.myHole`) arrives:
 * followed only while the user has not navigated on this device yet — after that the device's
 * own position is the truth and the live updates take over.
 */
export const resolveInitialPositionTarget = (
  myHole: number | null | undefined,
  context: { currentHole: number; userHasNavigated: boolean; holesCount: number },
): number | null => {
  if (context.userHasNavigated) return null;
  if (typeof myHole !== 'number' || !Number.isInteger(myHole) || myHole < 1 || myHole > context.holesCount) {
    return null;
  }
  return myHole === context.currentHole ? null : myHole;
};

/**
 * Per-round hole-sync state for this page session.
 *
 * None of this can live in a component ref. HoleScreen remounts on every hole change — the hole
 * is a route segment — and on every page load, so a ref would forget, on each remount, that the
 * user has taken a position in this round: the screen would stop claiming their navigations and
 * would re-apply the shared position over the hole they are actually looking at.
 */
type RoundSyncSession = {
  hasLocalPosition: boolean;
  lastLocalNavigationAt: number;
  resumePending: boolean;
  passiveHoles: Set<number>;
};

const sessions = new Map<string, RoundSyncSession>();

const sessionFor = (roundId: string): RoundSyncSession => {
  let session = sessions.get(roundId);
  if (!session) {
    session = { hasLocalPosition: false, lastLocalNavigationAt: 0, resumePending: false, passiveHoles: new Set() };
    sessions.set(roundId, session);
  }
  return session;
};

/**
 * The round is being opened from outside it (the round list, the round-start screen). Only such
 * an entry may resume the position shared with the user's other devices — a deep link, a reload
 * or a tap through to the next hole all name the hole they want, and that hole wins.
 */
export const markRoundEntry = (roundId: string): void => {
  sessionFor(roundId).resumePending = true;
};

/** Whether this mount may resume the shared position; true once per entry. */
export const consumeRoundEntry = (roundId: string): boolean => {
  const session = sessionFor(roundId);
  const pending = session.resumePending;
  session.resumePending = false;
  return pending;
};

/** The user navigated or scored here, so this device now holds a position in the round. */
export const markLocalNavigation = (roundId: string): void => {
  const session = sessionFor(roundId);
  session.hasLocalPosition = true;
  session.lastLocalNavigationAt = Date.now();
};

/** A hole this device is about to show by following, rather than by the user's own choice. */
export const markPassiveHole = (roundId: string, hole: number): void => {
  sessionFor(roundId).passiveHoles.add(hole);
};

/** Whether this hole is being shown passively; consumed, so a later visit counts as a choice. */
export const consumePassiveHole = (roundId: string, hole: number): boolean =>
  sessionFor(roundId).passiveHoles.delete(hole);

export const hasLocalPosition = (roundId: string): boolean => sessionFor(roundId).hasLocalPosition;

/**
 * The hole.view frame a device resends after a socket reconnect. Always passive: the resend
 * re-requests the hole's state, it is not a navigation. The user's position was recorded when
 * they actually navigated; an active claim here would re-assert a stale hole on every radio
 * drop — a watch drops constantly — dragging the user's other devices back to it.
 */
export const reconnectHoleView = (
  roundId: string,
  hole: number,
  deviceId: string,
): { type: 'hole.view'; roundId: string; hole: number; deviceId: string; passive: true } => ({
  type: 'hole.view',
  roundId,
  hole,
  deviceId,
  passive: true,
});

export const lastLocalNavigationAt = (roundId: string): number => sessionFor(roundId).lastLocalNavigationAt;

/** Test seam. */
export const __resetHoleSyncSessions = (): void => {
  sessions.clear();
};
